// K11: 自定义语音播放器 —— 替代浏览器原生 <audio controls>
//
// 视觉: 黑白专业风, 圆角胶囊
//   [▶/⏸] 0:03 / 0:12  ━━━━●━━━━━  🔊
//   filename (灰小字)

import { useEffect, useRef, useState } from "react";

interface VoicePlayerProps {
  src: string;
  name: string;
}

export function VoicePlayer({ src, name }: VoicePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [muted, setMuted] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      if (!dragging) setPos(a.currentTime);
    };
    const onMeta = () => setDur(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      setPos(0);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("durationchange", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("durationchange", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [dragging]);

  function toggle() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      void a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }

  function seek(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x =
      "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const ratio = Math.max(0, Math.min(1, (x - rect.left) / rect.width));
    const t = ratio * dur;
    a.currentTime = t;
    setPos(t);
  }

  function toggleMute() {
    const a = audioRef.current;
    if (!a) return;
    a.muted = !a.muted;
    setMuted(a.muted);
  }

  const ratio = dur > 0 ? Math.min(1, pos / dur) : 0;

  return (
    <div className="voice-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="voice-player__main">
        <button
          type="button"
          className="voice-player__btn voice-player__play"
          onClick={toggle}
          aria-label={playing ? "暂停" : "播放"}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
              <rect x="6" y="5" width="4" height="14" fill="currentColor" />
              <rect x="14" y="5" width="4" height="14" fill="currentColor" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
              <polygon points="6,4 20,12 6,20" fill="currentColor" />
            </svg>
          )}
        </button>
        <span className="voice-player__time">
          {fmt(pos)} <span className="voice-player__time-sep">/</span> {fmt(dur)}
        </span>
        <div
          className="voice-player__progress"
          onMouseDown={(e) => {
            setDragging(true);
            seek(e);
          }}
          onMouseUp={() => setDragging(false)}
          onMouseLeave={() => setDragging(false)}
          onMouseMove={(e) => {
            if (dragging) seek(e);
          }}
          onTouchStart={(e) => {
            setDragging(true);
            seek(e);
          }}
          onTouchEnd={() => setDragging(false)}
          onTouchMove={(e) => {
            if (dragging) seek(e);
          }}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={dur}
          aria-valuenow={pos}
        >
          <div className="voice-player__track" />
          <div
            className="voice-player__fill"
            style={{ width: `${ratio * 100}%` }}
          />
          <div
            className="voice-player__thumb"
            style={{ left: `${ratio * 100}%` }}
          />
        </div>
        <button
          type="button"
          className="voice-player__btn voice-player__mute"
          onClick={toggleMute}
          aria-label={muted ? "取消静音" : "静音"}
          title={muted ? "取消静音" : "静音"}
        >
          {muted ? "🔇" : "🔊"}
        </button>
      </div>
      <div className="voice-player__name muted small">{name}</div>
    </div>
  );
}

function fmt(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}
